import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import API_BASE_URL from "../api";
import { authFetch } from "../authFetch";
import { useAuth } from "../authContext";
import { useTranslation } from "react-i18next";
import { getLangFromPath, buildPathWithLang } from "../i18n/lang";
import { fmtDateTime, fmtMoney } from "../i18n/format";

export default function BillingPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [packages, setPackages] = useState([]);
  const [price, setPrice] = useState(1500);
  const { isAuth } = useAuth();
  const { t } = useTranslation('common');
  const loc = useLocation();
  const lang = getLangFromPath(loc.pathname);

  // редирект, если не авторизован
  useEffect(() => {
    if (!isAuth) navigate(buildPathWithLang(lang, "/login"));
  }, [isAuth, navigate, lang]);

  async function fetchPackages() {
    try {
      const res = await authFetch(`${API_BASE_URL}/monitoring-packages`);
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      setPackages(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error("fetchPackages error:", e);
    }
  }

  useEffect(() => {
    if (isAuth) fetchPackages();
  }, [isAuth]);

  async function createPackage() {
    setLoading(true);
    try {
      const res = await authFetch(`${API_BASE_URL}/monitoring-packages`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ price: Number(price) }),
      });
      if (!res.ok) throw new Error(await res.text());
      await fetchPackages();
    } catch (e) {
      alert(t('billing.create_error') + ': ' + e.message);
    } finally {
      setLoading(false);
    }
  }

  async function payPackage(id) {
    setLoading(true);
    try {
      const res = await authFetch(
        `${API_BASE_URL}/monitoring-packages/${id}/pay`,
        { method: "POST" }
      );
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data?.detail || JSON.stringify(data));
      }
      // монобанк повертає invoiceId/pageUrl
      if (data.pageUrl) {
        window.open(data.pageUrl, "_blank", "noopener,noreferrer");
      }
      // оновити список (статус може змінитись після вебхука)
      await fetchPackages();
    } catch (e) {
      alert(t('billing.pay_error') + ': ' + e.message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div style={{ maxWidth: 980 }}>
      <h2 style={{ marginTop: 0 }}>{t('billing.title')}</h2>

      {/* Створити пакет */}
      <div
        style={{
          background: "#fff",
          border: "1px solid #ddd",
          borderRadius: 12,
          padding: 16,
          marginBottom: 20,
        }}
      >
        <div style={{ fontWeight: 600, marginBottom: 8 }}>
          {t('billing.create_block.title')}
        </div>
        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          <label>{t('billing.create_block.price_label')}</label>
          <input
            type="number"
            min="1"
            value={price}
            onChange={(e) => setPrice(e.target.value)}
            style={{
              width: 140,
              padding: 8,
              borderRadius: 8,
              border: "1px solid #ccc",
            }}
          />
          <button
            onClick={createPackage}
            disabled={loading}
            style={{
              padding: "8px 14px",
              borderRadius: 10,
              border: "none",
              background: "rgb(0,110,69)",
              color: "#fff",
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            {t('billing.create_block.create_btn')}
          </button>
        </div>
      </div>

      {/* Список пакетів */}
      <div
        style={{
          background: "#fff",
          border: "1px solid #ddd",
          borderRadius: 12,
          padding: 16,
        }}
      >
        <div style={{ fontWeight: 600, marginBottom: 10 }}>{t('billing.list.title')}</div>
        {packages.length === 0 ? (
          <div style={{ color: "#666" }}>{t('billing.list.empty')}</div>
        ) : (
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ textAlign: "left" }}>
                <th style={{ padding: 8, borderBottom: "1px solid #eee" }}>{t('billing.table.id')}</th>
                <th style={{ padding: 8, borderBottom: "1px solid #eee" }}>
                  {t('billing.table.status')}
                </th>
                <th style={{ padding: 8, borderBottom: "1px solid #eee" }}>
                  {t('billing.table.price')}
                </th>
                <th style={{ padding: 8, borderBottom: "1px solid #eee" }}>
                  {t('billing.table.start')}
                </th>
                <th style={{ padding: 8, borderBottom: "1px solid #eee" }}>
                  {t('billing.table.end')}
                </th>
                <th style={{ padding: 8, borderBottom: "1px solid #eee" }}>
                  {t('billing.table.action')}
                </th>
              </tr>
            </thead>
            <tbody>
              {packages.map((p) => (
                <tr key={p.id}>
                  <td style={{ padding: 8, borderBottom: "1px solid #f3f3f3" }}>
                    {p.id}
                  </td>
                  <td style={{ padding: 8, borderBottom: "1px solid #f3f3f3" }}>
                    {p.status} {p.payment_status ? `(${p.payment_status})` : ""}
                  </td>
                  <td style={{ padding: 8, borderBottom: "1px solid #f3f3f3" }}>
                    {fmtMoney(p.price ?? 0, 'UAH')}
                  </td>
                  <td style={{ padding: 8, borderBottom: "1px solid #f3f3f3" }}>
                    {p.start_date ? fmtDateTime(p.start_date) : t('billing.table.na')}
                  </td>
                  <td style={{ padding: 8, borderBottom: "1px solid #f3f3f3" }}>
                    {p.end_date ? fmtDateTime(p.end_date) : t('billing.table.na')}
                  </td>
                  <td style={{ padding: 8, borderBottom: "1px solid #f3f3f3" }}>
                    {p.status !== "active" ? (
                      <button
                        onClick={() => payPackage(p.id)}
                        disabled={loading}
                        style={{
                          padding: "6px 12px",
                          borderRadius: 10,
                          border: "none",
                          background: "#006e45",
                          color: "#fff",
                          fontWeight: 600,
                          cursor: "pointer",
                        }}
                      >
                        {t('billing.pay')}
                      </button>
                    ) : (
                      <span style={{ color: "#006e45", fontWeight: 600 }}>
                        {t('billing.active')}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
